import React, { useState, useEffect } from 'react';
import { Mic, Camera, MapPin, FolderOpen, Shield, Check, X } from 'lucide-react';
import { isMobileApp } from '../utils/platform';

const STORAGE_KEY = 'nn_permissions_v1';

const PERMISSIONS = [
  {
    key: 'mic',
    icon: Mic,
    label: 'Microphone',
    reason: 'Voice messages in chat',
    color: 'text-amber-500',
    bg: 'bg-amber-50',
  },
  {
    key: 'camera',
    icon: Camera,
    label: 'Camera',
    reason: 'Photo sharing & AI image analysis',
    color: 'text-blue-500',
    bg: 'bg-blue-50',
  },
  {
    key: 'files',
    icon: FolderOpen,
    label: 'Files & Gallery',
    reason: 'Share documents and images in chat',
    color: 'text-green-500',
    bg: 'bg-green-50',
  },
  {
    key: 'location',
    icon: MapPin,
    label: 'Location',
    reason: 'Personalise career advice by region',
    color: 'text-rose-500',
    bg: 'bg-rose-50',
  },
];

const PermissionRequest = () => {
  const [show, setShow] = useState(false);
  const [requesting, setRequesting] = useState(false);
  const [statuses, setStatuses] = useState({});

  const isNativeApp = isMobileApp();

  // Never auto-show on page load — only trigger when a feature explicitly
  // needs a permission (mic/camera in Friends Chat, etc.)
  // useEffect auto-show block intentionally removed.

  // Re-triggered from anywhere a feature needs a permission:
  // window.dispatchEvent(new CustomEvent('nn-request-permissions'))
  useEffect(() => {
    if (isNativeApp) return; // Skip on mobile app
    const handler = () => setShow(true);
    window.addEventListener('nn-request-permissions', handler);
    return () => window.removeEventListener('nn-request-permissions', handler);
  }, []);

  const setStatus = (key, val) =>
    setStatuses(prev => ({ ...prev, [key]: val }));

  const requestAll = async () => {
    setRequesting(true);

    // Microphone
    try {
      const s = await navigator.mediaDevices.getUserMedia({ audio: true });
      s.getTracks().forEach(t => t.stop());
      setStatus('mic', 'granted');
    } catch {
      setStatus('mic', 'denied');
    }

    // Camera
    try {
      const s = await navigator.mediaDevices.getUserMedia({ video: true });
      s.getTracks().forEach(t => t.stop());
      setStatus('camera', 'granted');
    } catch {
      setStatus('camera', 'denied');
    }

    // Files — browser handles natively on file input click
    setStatus('files', 'granted');

    // Location
    try {
      await new Promise((res, rej) =>
        navigator.geolocation.getCurrentPosition(res, rej, { timeout: 8000 })
      );
      setStatus('location', 'granted');
    } catch {
      setStatus('location', 'denied');
    }

    localStorage.setItem(STORAGE_KEY, 'done');
    setRequesting(false);
    setTimeout(() => {
      setShow(false);
      window.dispatchEvent(new CustomEvent('nn-permissions-closed'));
    }, 1200);
  };

  // "Not Now" — store 'skipped' so it won't auto-show again on reload,
  // but it CAN still reappear when a feature explicitly needs a permission.
  const dismiss = () => {
    localStorage.setItem(STORAGE_KEY, 'skipped');
    setShow(false);
    window.dispatchEvent(new CustomEvent('nn-permissions-closed'));
  };

  if (isNativeApp || !show) return null;

  return (
    <div
      className="fixed inset-0 z-[9999] flex items-end sm:items-center justify-center"
      style={{ background: 'rgba(0,0,0,0.6)', backdropFilter: 'blur(4px)' }}
    >
      <div className="w-full sm:max-w-sm bg-white rounded-t-3xl sm:rounded-2xl shadow-2xl overflow-hidden animate-slide-up">
        {/* Header */}
        <div className="bg-gradient-to-r from-amber-600 to-orange-600 px-6 pt-6 pb-5 text-white relative">
          <button
            onClick={dismiss}
            className="absolute top-3 right-3 w-7 h-7 flex items-center justify-center rounded-full text-white/70 hover:text-white hover:bg-white/20 transition-colors"
            aria-label="Close"
          >
            <X className="w-4 h-4" />
          </button>
          <div className="flex items-center space-x-3">
            <div className="w-10 h-10 rounded-full bg-white/20 flex items-center justify-center">
              <Shield className="w-5 h-5" />
            </div>
            <div>
              <h2 className="font-bold text-lg leading-tight">App Permissions</h2>
              <p className="text-white/80 text-sm">For the best experience</p>
            </div>
          </div>
        </div>

        {/* Permission list */}
        <div className="px-6 py-4 space-y-3">
          {PERMISSIONS.map(({ key, icon: Icon, label, reason, color, bg }) => {
            const s = statuses[key];
            return (
              <div
                key={key}
                className={`flex items-center space-x-3 p-3 rounded-xl ${bg} transition-all`}
              >
                <div className="w-9 h-9 rounded-full bg-white flex items-center justify-center flex-shrink-0 shadow-sm">
                  {s === 'granted' ? (
                    <Check className="w-4 h-4 text-green-500" />
                  ) : s === 'denied' ? (
                    <X className="w-4 h-4 text-red-400" />
                  ) : (
                    <Icon className={`w-4 h-4 ${color}`} />
                  )}
                </div>
                <div className="min-w-0 flex-1">
                  <p className="font-semibold text-gray-900 text-sm">{label}</p>
                  <p className="text-gray-500 text-xs">{reason}</p>
                </div>
              </div>
            );
          })}
        </div>

        {/* Actions */}
        <div
          className="px-6 pb-6 space-y-2"
          style={{ paddingBottom: 'max(1.5rem, env(safe-area-inset-bottom, 1.5rem))' }}
        >
          <button
            onClick={requestAll}
            disabled={requesting}
            data-testid="allow-permissions-btn"
            className="w-full py-3.5 rounded-xl font-semibold text-white text-sm bg-gradient-to-r from-amber-600 to-orange-600 hover:from-amber-700 hover:to-orange-700 disabled:opacity-60 transition-all active:scale-95"
          >
            {requesting ? (
              <span className="flex items-center justify-center space-x-2">
                <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                <span>Requesting access…</span>
              </span>
            ) : (
              'Allow Access'
            )}
          </button>
          <button
            onClick={dismiss}
            data-testid="skip-permissions-btn"
            className="w-full py-2.5 text-gray-500 text-sm font-medium hover:text-gray-700 transition-colors"
          >
            Not Now
          </button>
        </div>
      </div>
    </div>
  );
};

export default PermissionRequest;
